import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Clock, Trash2, Plus } from 'lucide-react';
import mapi from './api';
import { useMobile } from './MobileContext';
import { MobileShell, MobileHeader, C, AppBottomNav } from './MobileShell';
import { isStaffArea } from './areas';
import { formatDeNumber } from '@/lib/utils';

const inputCls = 'w-full rounded-lg px-3 py-2.5 text-sm outline-none';
const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text };

const STATUS = {
  gemeldet: { label: 'Gemeldet', bg: '#78350f', fg: '#fcd34d' },
  genehmigt: { label: 'Genehmigt', bg: '#065f46', fg: '#6ee7b7' },
  abgelehnt: { label: 'Abgelehnt', bg: '#7f1d1d', fg: '#fca5a5' },
  abgerechnet: { label: 'Abgerechnet', bg: '#374151', fg: '#d1d5db' },
};

const emptyForm = () => ({
  work_date: new Date().toISOString().slice(0, 10),
  hours: '1',
  activity: '',
  notes: '',
});

const fmtHours = (h) => formatDeNumber(h);
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};

export default function MobilePlusHours() {
  const { me, loading: meLoading, area } = useMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await mapi.get('/teacher/plus-hours');
      setRows(r.data || []);
      await mapi.post('/app/notifications/seen', { area: 'plus_hours' }).catch(() => {});
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Plusstunden konnten nicht geladen werden';
      setError(typeof msg === 'string' ? msg : 'Laden fehlgeschlagen');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (me && isStaffArea(area)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, area]);

  if (meLoading) {
    return <MobileShell><div className="flex-1 flex items-center justify-center" style={{ color: C.dim }}>Laden…</div></MobileShell>;
  }
  if (!me) return <Navigate to="/m/login" replace />;
  if (!isStaffArea(area)) return <Navigate to="/m" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const hours = parseFloat(String(form.hours).replace(',', '.'));
    if (!form.work_date || !form.activity.trim() || !(hours > 0)) {
      toast.error('Datum, Tätigkeit und Stunden (> 0) sind erforderlich');
      return;
    }
    setSaving(true);
    try {
      await mapi.post('/teacher/plus-hours', {
        work_date: form.work_date,
        hours,
        activity: form.activity.trim(),
        notes: form.notes.trim() || null,
      });
      toast.success('Plusstunden gemeldet');
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Meldung fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (id) => {
    if (!window.confirm('Offene Meldung zurückziehen?')) return;
    try {
      await mapi.delete(`/teacher/plus-hours/${id}`);
      toast.success('Meldung zurückgezogen');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <MobileShell bottomNav={<AppBottomNav />}>
      <MobileHeader title="Plusstunden" backTo="/m" onRefresh={load} />
      <div className="px-4 py-4 space-y-4 pb-8">
        <p className="text-xs" style={{ color: C.dim }}>
          Zusatzaufwand außerhalb des Regelunterrichts. Die Verwaltung prüft die Meldung;
          genehmigte Stunden fließen in die Lohnübersicht (Lohn 1).
        </p>

        <form onSubmit={submit} className="rounded-xl p-4 space-y-3"
              style={{ background: C.card, border: `1px solid ${C.border}` }}
              data-testid="mobile-plus-hours-form">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4" style={{ color: C.red }} />
            <span className="text-sm font-semibold">Neue Meldung</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>Datum</label>
              <input type="date" required value={form.work_date}
                     onChange={(e) => setForm({ ...form, work_date: e.target.value })}
                     className={`${inputCls} mt-1`} style={inputStyle}
                     data-testid="mobile-plus-hours-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>Stunden</label>
              <input required inputMode="decimal" value={form.hours}
                     onChange={(e) => setForm({ ...form, hours: e.target.value })}
                     placeholder="z. B. 1,5"
                     className={`${inputCls} mt-1`} style={inputStyle}
                     data-testid="mobile-plus-hours-hours" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>Tätigkeit</label>
            <input required value={form.activity}
                   onChange={(e) => setForm({ ...form, activity: e.target.value })}
                   placeholder="z. B. Konzert, Elternabend, Orga"
                   className={`${inputCls} mt-1`} style={inputStyle}
                   data-testid="mobile-plus-hours-activity" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide" style={{ color: C.dim }}>Bemerkung (optional)</label>
            <textarea value={form.notes} rows={2}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className={`${inputCls} mt-1 resize-none`} style={inputStyle}
                      data-testid="mobile-plus-hours-notes" />
          </div>
          <button type="submit" disabled={saving}
                  className="w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: C.red }}
                  data-testid="mobile-plus-hours-submit">
            <Plus className="w-4 h-4" />
            {saving ? 'Wird gemeldet…' : 'Melden'}
          </button>
        </form>

        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}
             data-testid="mobile-plus-hours-list">
          <div className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wide"
               style={{ background: C.card, color: C.dim, borderBottom: `1px solid ${C.border}` }}>
            Meine Meldungen
          </div>
          {loading ? (
            <div className="py-8 text-center text-sm" style={{ color: C.dim }}>Laden…</div>
          ) : error ? (
            <div className="px-4 py-6 text-sm text-center" style={{ color: '#fca5a5' }}>{error}</div>
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-sm" style={{ color: C.dim }}>Noch keine Plusstunden gemeldet</div>
          ) : (
            <ul>
              {rows.map((r) => {
                const st = STATUS[r.status] || STATUS.gemeldet;
                return (
                  <li key={r.id} className="px-4 py-3 flex items-start gap-3"
                      style={{ background: C.card, borderBottom: `1px solid ${C.border}` }}
                      data-testid={`mobile-plus-hours-row-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{fmtDate(r.work_date)}</span>
                        <span className="text-sm tabular-nums" style={{ color: C.dim }}>{fmtHours(r.hours)} Std.</span>
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                              style={{ background: st.bg, color: st.fg }}>{st.label}</span>
                      </div>
                      <p className="text-sm mt-0.5 truncate">{r.activity}</p>
                      {r.notes && <p className="text-xs mt-0.5" style={{ color: C.dim }}>{r.notes}</p>}
                      {r.review_notes && (
                        <p className="text-xs mt-1 italic" style={{ color: C.dim }}>Verwaltung: {r.review_notes}</p>
                      )}
                    </div>
                    {r.status === 'gemeldet' && (
                      <button onClick={() => withdraw(r.id)}
                              className="p-2 rounded-lg shrink-0"
                              style={{ color: C.dim }}
                              data-testid={`mobile-plus-hours-delete-${r.id}`}
                              aria-label="Zurückziehen">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </MobileShell>
  );
}
